import {useState, useCallback} from 'react';
import axiosWidgetInstance, {withApiKey, widgetEndpoints} from 'packages/common/utils/axiosWidget';
import useSWR from 'swr';

/**
 * @file Hooks de réservation de table pour le widget embarqué (auth par clé API).
 * @module packages.common.hooks.api.widgets.restaurants.Reservations
 *
 * Câblé sur `axiosWidget` et les endpoints `externalApi.restaurant.reservation.*`,
 * la clé API étant injectée via `withApiKey`. La revalidation automatique SWR est
 * désactivée pour limiter le trafic des widgets tiers. Les hooks publics
 * équivalents (mêmes shapes de réponse) vivent dans `api/public/restaurants/Reservation`.
 */

/**
 * Récupère la configuration de réservation d'un établissement via l'API externe (SWR).
 *
 * S'abonne à `GET reservation.config(companyId)` et expose `data.data`. Le
 * chargement ne démarre que si `companyId` est fourni.
 *
 * @param {string} companyId - Identifiant de l'établissement (null tant qu'inconnu)
 * @param {string} apiKey - Clé API d'authentification du widget
 * @return {Object} `{config, isLoading, isError, error, mutate}`
 * @return {Object|undefined} return.config - Configuration de réservation déballée
 * @return {boolean} return.isLoading - Chargement initial en cours
 * @return {boolean} return.isError - Présence d'une erreur
 * @return {Error|undefined} return.error - Erreur éventuelle
 * @return {Function} return.mutate - Invalide/recharge le cache
 */
export const useExternalReservationConfig = (companyId, apiKey) => {
  const endpointUrl = companyId ? widgetEndpoints.externalApi.restaurant.reservation.config(companyId) : null;

  const fetcher = async ([url, key]) => {
    try {
      const response = await axiosWidgetInstance.get(url, withApiKey(key));
      return response.data;
    } catch (error) {
      // Les logs d'erreur sont gérés par l'intercepteur axios.
      throw error;
    }
  };

  const {data, error, mutate} = useSWR(
    endpointUrl ? [endpointUrl, apiKey] : null,
    fetcher,
    {
      errorRetryCount: 2,
      errorRetryInterval: 5000,
      // Revalidation auto désactivée : limite le trafic des widgets tiers.
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return {
    config: data?.data,
    isLoading: endpointUrl !== null && !error && !data,
    isError: !!error,
    error,
    mutate,
  };
};

/**
 * Récupère impérativement les dates disponibles via l'API externe.
 *
 * Appelle `GET reservation.dates(companyId)` (query `service`, `partySize`,
 * `year?`, `month?`) et déballe `data.data.dates`.
 *
 * @return {Object} `{getAvailableDates, loading, error}`
 * @return {Function} return.getAvailableDates - `(companyId, params, apiKey) => Promise<Array>` résout les dates disponibles
 * @return {boolean} return.loading - Chargement en cours
 * @return {Error|null} return.error - Erreur éventuelle
 * @throws {Error} Si `service` ou `partySize` manquent
 */
export const useExternalAvailableDates = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getAvailableDates = async (companyId, params, apiKey) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();

      if (!params.service || !params.partySize) {
        throw new Error('service et partySize sont requis');
      }

      queryParams.append('service', params.service);
      queryParams.append('partySize', params.partySize);

      if (params.year) queryParams.append('year', params.year);
      if (params.month) queryParams.append('month', params.month);

      const queryString = `?${queryParams.toString()}`;
      const url = `${widgetEndpoints.externalApi.restaurant.reservation.dates(companyId)}${queryString}`;

      const response = await axiosWidgetInstance.get(url, withApiKey(apiKey));
      const dates = response.data?.data?.dates || [];

      return dates;
    } catch (apiError) {
      console.error('Error fetching available dates:', apiError.message);
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  return {getAvailableDates, loading, error};
};

/**
 * Récupère impérativement les prochaines dates réservables via l'API externe.
 *
 * Appelle `GET reservation.nextDates(companyId)` (query `service`, `partySize`,
 * `from?`, `limit?`) et déballe `data.data` : `{dates, horizon, timezone}`.
 * Accepte un `signal` d'annulation : le bandeau du calendrier abandonne la
 * requête en vol dès que le client change de mois, de service ou de convives.
 *
 * @return {Object} `{getNextAvailableDates}`
 * @return {Function} return.getNextAvailableDates - `(companyId, params, apiKey, {signal}) => Promise<Object>`
 * @throws {Error} Si `service` ou `partySize` manquent
 */
export const useExternalNextAvailableDates = () => {
  const getNextAvailableDates = useCallback(async (companyId, params, apiKey, {signal} = {}) => {
    if (!params?.service || !params?.partySize) {
      throw new Error('service et partySize sont requis');
    }

    const response = await axiosWidgetInstance.get(
        widgetEndpoints.externalApi.restaurant.reservation.nextDates(companyId),
        {
          ...withApiKey(apiKey),
          params: {service: params.service, partySize: params.partySize, from: params.from, limit: params.limit},
          signal,
        },
    );
    return response.data?.data || null;
  }, []);

  return {getNextAvailableDates};
};

/**
 * Récupère impérativement les créneaux horaires d'une date via l'API externe.
 *
 * Appelle `GET reservation.timeslots(companyId)` (query `date`, `service`,
 * `partySize`) et déballe `data.data` (payload `{timeslots, serviceNote?, ...}`).
 *
 * @return {Object} `{getTimeSlotsForDate, loading, error}`
 * @return {Function} return.getTimeSlotsForDate - `(companyId, params, apiKey) => Promise<Object>` résout le payload créneaux
 * @return {boolean} return.loading - Chargement en cours
 * @return {Error|null} return.error - Erreur éventuelle
 * @throws {Error} Si `date`, `service` ou `partySize` manquent
 */
export const useExternalTimeSlots = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getTimeSlotsForDate = async (companyId, params, apiKey) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();

      if (!params.date || !params.service || !params.partySize) {
        throw new Error('date, service et partySize sont requis');
      }

      queryParams.append('date', params.date);
      queryParams.append('service', params.service);
      queryParams.append('partySize', params.partySize);
      // Choix de salle : restreint les créneaux à la salle demandée si fournie.
      if (params.areaId) queryParams.append('areaId', params.areaId);

      const queryString = `?${queryParams.toString()}`;
      const url = `${widgetEndpoints.externalApi.restaurant.reservation.timeslots(companyId)}${queryString}`;

      const response = await axiosWidgetInstance.get(url, withApiKey(apiKey));
      return response.data?.data || {timeslots: []};
    } catch (apiError) {
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  return {getTimeSlotsForDate, loading, error};
};

/**
 * Récupère impérativement les salles réservables en ligne via l'API externe.
 *
 * Appelle `GET reservation.rooms(companyId)` (query `date?`, `service?`,
 * `partySize?`) et déballe `data.data.rooms`. Avec le contexte de créneau, chaque
 * salle est enrichie de sa disponibilité (`available`).
 *
 * @return {Object} `{getBookableRooms, loading, error}`
 * @return {Function} return.getBookableRooms - `(companyId, params, apiKey) => Promise<Array>` résout les salles
 * @return {boolean} return.loading - Chargement en cours
 * @return {Error|null} return.error - Erreur éventuelle
 */
export const useExternalBookableRooms = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getBookableRooms = async (companyId, params = {}, apiKey) => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (params.date) queryParams.append('date', params.date);
      if (params.service) queryParams.append('service', params.service);
      if (params.partySize) queryParams.append('partySize', params.partySize);

      const query = queryParams.toString();
      const base = widgetEndpoints.externalApi.restaurant.reservation.rooms(companyId);
      const url = query ? `${base}?${query}` : base;

      const response = await axiosWidgetInstance.get(url, withApiKey(apiKey));
      return response.data?.data?.rooms || [];
    } catch (apiError) {
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  return {getBookableRooms, loading, error};
};

/**
 * Crée impérativement une réservation via l'API externe.
 *
 * Appelle `POST reservation.create(companyId)` et résout `data` (réponse JSON
 * brute du backend).
 *
 * @return {Object} `{createReservation, loading, error}`
 * @return {Function} return.createReservation - `(companyId, data, apiKey) => Promise<Object>`
 * @return {boolean} return.loading - Chargement en cours
 * @return {Error|null} return.error - Erreur éventuelle
 */
export const useExternalCreateReservation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createReservation = async (companyId, data, apiKey) => {
    setLoading(true);
    setError(null);

    try {
      const reservationData = {
        ...data,
        // `dateTime` reste la string ISO fournie par le flow : le backend attend
        // cette chaîne, pas un objet Date relocalisé dans un autre fuseau.
        dateTime: data.dateTime,
        numberOfGuests: parseInt(data.numberOfGuests, 10) || 1,
        duration: data.duration ? parseInt(data.duration, 10) : undefined,
      };

      const response = await axiosWidgetInstance.post(
          widgetEndpoints.externalApi.restaurant.reservation.create(companyId),
          reservationData,
          withApiKey(apiKey),
      );

      return response.data;
    } catch (apiError) {
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  };

  return {createReservation, loading, error};
};
